import { useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Home, 
  Search, 
  Construction,
  FileQuestion,
  Rocket,
  Code,
  Sparkles,
  Clock,
  AlertCircle
} from "lucide-react";

const NotFound = () => {
  const location = useLocation();
  const [isUnderDevelopment, setIsUnderDevelopment] = useState(false);
  const [sentryEventId, setSentryEventId] = useState<string | null>(null);

  // Routes that are under development
  const underDevelopmentRoutes = [
    '/ai-assistant',
    '/email-marketing',
    '/advanced-features',
    '/premium-templates'
  ];

  useEffect(() => {
    // Check if route is under development
    const isDevelopment = underDevelopmentRoutes.some(route => 
      location.pathname.startsWith(route)
    );
    setIsUnderDevelopment(isDevelopment);

    // Log to console
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );

    // Send 404 error to Sentry with context
    const eventId = Sentry.captureMessage(
      isDevelopment 
        ? `Under Development: ${location.pathname}` 
        : `404 Not Found: ${location.pathname}`,
      {
        level: isDevelopment ? 'info' : 'warning',
        tags: {
          type: isDevelopment ? 'under_development' : '404',
          route: location.pathname,
        },
        contexts: {
          page: {
            path: location.pathname,
            referrer: document.referrer || 'direct',
            search: location.search,
            hash: location.hash,
          },
          user_agent: {
            browser: navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
          }
        },
        fingerprint: ['404', location.pathname], // Group by path
      }
    );

    setSentryEventId(eventId);

  }, [location.pathname]);

  // Handle user feedback
  const handleReportIssue = () => {
    if (sentryEventId) {
      Sentry.showReportDialog({ 
        eventId: sentryEventId,
        title: 'Help us improve',
        subtitle: 'What were you looking for?',
        subtitle2: 'Tell us what happened.',
        labelName: 'Name',
        labelEmail: 'Email',
        labelComments: 'What happened?',
        labelSubmit: 'Submit Feedback',
        successMessage: 'Thank you for your feedback!',
      });
    } else {
      // Alternative: Capture new feedback without event
      Sentry.captureFeedback({
        name: 'User',
        message: `Looking for: ${location.pathname}`,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-4 flex items-center justify-center relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-theme-gradient opacity-10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-2xl w-full text-center space-y-8 relative z-10">
        {/* Main Card */}
        <Card className="glass-card border-0 p-8 md:p-12 space-y-6 backdrop-blur-xl">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-theme-gradient opacity-20 blur-2xl rounded-full animate-pulse"></div>
              <div className="relative w-24 h-24 rounded-full bg-gradient-primary flex items-center justify-center">
                {isUnderDevelopment ? (
                  <Construction className="h-12 w-12 text-white animate-bounce" />
                ) : (
                  <FileQuestion className="h-12 w-12 text-white" />
                )}
              </div>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex justify-center">
            <Badge 
              variant={isUnderDevelopment ? "default" : "destructive"} 
              className={isUnderDevelopment ? "bg-gradient-primary" : ""}
            >
              {isUnderDevelopment ? (
                <>
                  <Rocket className="h-3 w-3 mr-1" />
                  Under Development
                </>
              ) : (
                <>
                  <FileQuestion className="h-3 w-3 mr-1" />
                  404 Error
                </>
              )}
            </Badge>
          </div>

          {/* Content */}
          <div className="space-y-4">
            <h1 className="text-6xl md:text-8xl font-bold gradient-text animate-pulse">
              {isUnderDevelopment ? "🚧" : "404"}
            </h1>
            
            <div className="space-y-3">
              <p className="text-2xl md:text-3xl font-bold">
                {isUnderDevelopment 
                  ? "We're Building Something Amazing!" 
                  : "Oops! Page Not Found"
                }
              </p>
              
              <p className="text-muted-foreground max-w-md mx-auto">
                {isUnderDevelopment ? (
                  <>
                    This feature is currently in our development pipeline. 
                    Our team is working hard to bring this to you soon!
                  </>
                ) : (
                  <>
                    The page you're looking for doesn't exist or has been moved. 
                    Let's get you back on track.
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Additional Info for Under Development */}
          {isUnderDevelopment && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Expected Launch: Coming Soon</span>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                <Badge variant="outline" className="glass-card border-0">
                  <Code className="h-3 w-3 mr-1" />
                  In Progress
                </Badge>
                <Badge variant="outline" className="glass-card border-0">
                  <Sparkles className="h-3 w-3 mr-1" />
                  AI-Powered
                </Badge>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
            <Button asChild className="bg-gradient-primary hover-glow" size="lg">
              <Link to="/">
                <Home className="mr-2 h-4 w-4" />
                Go to Homepage
              </Link>
            </Button>
            
            <Button asChild variant="outline" className="glass-card border-0" size="lg">
              <Link to="/dashboard">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Link>
            </Button>
          </div>

          {/* Report Issue Button - Sentry Integration */}
          <div className="pt-4 border-t border-border/40">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleReportIssue}
              className="text-muted-foreground hover:text-foreground"
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Report this issue
            </Button>
          </div>

          {/* Search Alternative */}
          <div className="pt-4">
            <p className="text-sm text-muted-foreground mb-3">
              Looking for something specific?
            </p>
            <div className="flex gap-2 max-w-md mx-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 bg-muted/50 border border-border/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-theme-primary transition-all"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const searchQuery = e.currentTarget.value;
                      // Track search attempts on 404 page
                      Sentry.addBreadcrumb({
                        category: '404-search',
                        message: `User searched for: ${searchQuery}`,
                        level: 'info',
                      });
                      // Implement search functionality
                      console.log('Search:', searchQuery);
                    }
                  }}
                />
              </div>
              <Button variant="outline" className="glass-card border-0">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Helpful Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Events', path: '/events', icon: '🎉' },
            { label: 'Templates', path: '/templates', icon: '📋' },
            { label: 'Analytics', path: '/analytics', icon: '📊' },
            { label: 'Settings', path: '/settings', icon: '⚙️' },
          ].map((link) => (
            <Link 
              key={link.path} 
              to={link.path}
              className="glass-card border-0 p-4 rounded-lg hover:bg-accent/50 transition-all hover-glow group"
              onClick={() => {
                // Track which link users click from 404 page
                Sentry.addBreadcrumb({
                  category: '404-navigation',
                  message: `User navigated to ${link.path} from 404`,
                  level: 'info',
                });
              }}
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">
                {link.icon}
              </div>
              <p className="text-sm font-medium">{link.label}</p>
            </Link>
          ))}
        </div>

        {/* Footer Note */}
        <p className="text-xs text-muted-foreground">
          Error Code: 404 | Path: <code className="bg-muted px-2 py-1 rounded text-xs">{location.pathname}</code>
          {sentryEventId && (
            <>
              {' '}| Event ID: <code className="bg-muted px-2 py-1 rounded text-xs">{sentryEventId.substring(0, 8)}...</code>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default NotFound;
